import * as React from 'react';
import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardContentProps,
  CardHeader as MuiCardHeader,
  CardHeaderProps,
  CardProps,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import {useStyles} from '../../components/Tabs/CardItem';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import makeStyles from '@mui/styles/makeStyles';

export const Card = styled(MuiCard)(({ theme }) => ({
  margin: theme.spacing(1, 0, 3, 0),
  borderTop: '2px solid ' + theme.palette.primary.main,
  boxShadow: '0 3px 1px -2px #0003, 0 2px 2px #00000024, 0 1px 5px #0000001f',
  overflow: 'visible',
  pb: 1,
  marginBottom: 18,
  paddingTop: 18
}));

const CardHeader = styled(MuiCardHeader)(({ theme }) => ({
  paddingBottom: theme.spacing(0),
  paddingTop: 0
}));
const CardContent = styled(MuiCardContent)(({ theme }) => ({
  paddingTop: 0,
  display: 'grid',
  overflow: 'hidden',
}));

type CustomCardItemProps = (HiddenChildrenProps | ChildrenProps) & {
  title?: string | JSX.Element;
  subLabel?: string | JSX.Element;
  contentProps?: Partial<CardContentProps>;
  cardProps?: Partial<CardProps>;
  headerProps?: Partial<CardHeaderProps>;
};

type HiddenChildrenProps = {
  useHiddenFields: true;
  showHidden?: boolean;
  children: (hidden: boolean) => React.ReactNode;
};

type ChildrenProps = {
  useHiddenFields?: false;
  showHidden?: false;
  children: React.ReactNode;
};

export const useCardResizeStyles =  makeStyles(theme => ({
  resizableBox: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '& .react-resizable-handle': {
      position: 'absolute',
      width: 20,
      height: 20,
      bottom: 0,
      right: 0,
      background:
          'url(\'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+\')',
      'background-position': 'bottom right',
      padding: '0 3px 3px 0',
      'background-repeat': 'no-repeat',
      'background-origin': 'content-box',
      'box-sizing': 'border-box',
      cursor: 'se-resize'
    }
  },
  resizableCard: {
    height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'hidden'
  }
}));

export const CardHeaderTitle = (
  props: Pick<CustomCardItemProps, 'title' | 'subLabel'>
) => {
  const classes = useStyles();
  return (
    <Typography
      style={{ position: 'relative', marginRight: '20px' }}
      gutterBottom
      variant="h5"
      component="div"
      className={classes.title}
    >
      {props.title}{' '}
      {props.subLabel && (
        <small className={classes.subLabel}>{props.subLabel}</small>
      )}
    </Typography>
  );
};

const CustomCardItem = (props: CustomCardItemProps) => {
  const {
    title,
    subLabel,
    contentProps,
    cardProps,
    headerProps,
    children,
    useHiddenFields = false,
    showHidden: defaultHidden = false,
  } = props;
  const [showHidden, setShowHidden] = React.useState(defaultHidden);

  React.useEffect(() => {
    setShowHidden(defaultHidden);
  }, [defaultHidden]);
  return (
    <Card {...cardProps} raised>
      {(title || headerProps || subLabel || useHiddenFields) && (
        <CardHeader
          title={
            <CardHeaderTitle title={title ?? ''} subLabel={subLabel ?? ''} />
          }
          action={
            useHiddenFields ? (
              <Tooltip
                title={!showHidden ? 'Vis mer' : 'Vis mindre'}
                placement="bottom"
              >
                <IconButton
                  aria-label="settings"
                  onClick={() => setShowHidden((prev) => !prev)}
                >
                  <MenuOpenIcon color="primary" />
                </IconButton>
              </Tooltip>
            ) : (
              headerProps?.action
            )
          }
          {...headerProps}
        />
      )}

      <CardContent {...contentProps}>
        {useHiddenFields && typeof children === 'function'
          ? children(!showHidden) as React.ReactNode
          : children as React.ReactNode}
      </CardContent>
    </Card>
  );
};

export default CustomCardItem;
